<template>
  <ul v-if="items">
    <template v-for="(item, i) of items">
      <li
        v-if="visible(item) && !item.separator"
        :key="i"
        :class="[
          {
            'active-menuitem': activeIndex === i && !item.to && !item.disabled,
          },
        ]"
        role="none"
      >
        <div v-if="item.items && root === true" class="arrow"></div>
        <router-link
          v-if="item.to || isInternalRoute(item)"
          :to="getItemRoute(item)"
          :class="[
            item.class,
            'p-ripple',
            { 'active-route': activeIndex === i, 'p-disabled': item.disabled },
          ]"
          :style="item.style"
          @click.native="onMenuItemClick($event, item, i)"
          @auxclick.native="onMenuItemAuxClick($event, item, i)"
          :target="item.target"
          exact
          role="menuitem"
          v-ripple
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
          <i
            v-if="item.items"
            class="pi pi-fw pi-angle-down menuitem-toggle-icon"
          ></i>
          <span v-if="item.badge" class="menuitem-badge">{{ item.badge }}</span>
        </router-link>
        <a
          v-if="!item.to && !isInternalRoute(item)"
          :href="item.url || '#'"
          :style="item.style"
          :class="[item.class, 'p-ripple', { 'p-disabled': item.disabled }]"
          @click="onMenuItemClick($event, item, i)"
          :target="item.target"
          role="menuitem"
          v-ripple
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
          <i
            v-if="item.items"
            class="pi pi-fw pi-angle-down menuitem-toggle-icon"
          ></i>
          <span v-if="item.badge" class="menuitem-badge">{{ item.badge }}</span>
        </a>
        <transition name="layout-submenu-wrapper">
          <AppSubmenu
            v-show="forceOpen || activeIndex === i"
            :items="visible(item) && item.items"
            :forceOpen="forceOpen"
            @menuitem-click="$emit('menuitem-click', $event)"
          ></AppSubmenu>
        </transition>
      </li>
      <li
        class="p-menu-separator"
        :style="item.style"
        v-if="visible(item) && item.separator"
        :key="'separator' + i"
        role="separator"
      ></li>
    </template>
  </ul>
</template>
<script>
import { isBackgroundIntent } from "@/helpers/appTabNavigation";

const INTERNAL_TABS_TEMP_DISABLED = true;

export default {
  name: "AppSubmenu",
  props: {
    items: Array,
    root: {
      type: Boolean,
      default: false,
    },
    // Pesquisa de páginas activa: todos os grupos ficam abertos.
    forceOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeIndex: null,
    };
  },
  created() {
    this.syncActiveIndexWithRoute();
  },
  watch: {
    "$route.path"() {
      this.syncActiveIndexWithRoute();
    },
    // A pesquisa do menu troca a árvore por uma cópia podada: os índices mudam.
    items() {
      this.syncActiveIndexWithRoute();
    },
  },
  methods: {
    routeMatches(item, path) {
      const target = item.to || (this.isInternalRoute(item) ? item.url : null);
      if (!target) {
        return false;
      }
      // Páginas de detalhe (/foo/:id) contam para o item /foo; a raiz não.
      return path === target || (target !== "/" && path.indexOf(`${target}/`) === 0);
    },
    containsRoute(item, path) {
      if (this.routeMatches(item, path)) {
        return true;
      }
      return (item.items || []).some(
        (child) => child && this.containsRoute(child, path)
      );
    },
    // Abre o grupo (e marca a folha) que contém a rota actual, para a sidebar
    // mostrar onde o utilizador está ao chegar por URL ou ao recarregar.
    syncActiveIndexWithRoute() {
      const path = this.$route ? this.$route.path : null;
      if (!path || !Array.isArray(this.items)) {
        return;
      }
      const index = this.items.findIndex(
        (item) =>
          item && !item.separator && this.visible(item) && this.containsRoute(item, path)
      );
      // Sem correspondência (página fora do menu) fecha-se o nível, para não
      // ficar um grupo aberto ou uma folha marcada da rota anterior.
      this.activeIndex = index !== -1 ? index : null;
    },
    isInternalRoute(item) {
      return (
        !item.to &&
        typeof item.url === "string" &&
        item.url.indexOf("/") === 0 &&
        (!item.target || item.target === "_self")
      );
    },
    getItemRoute(item) {
      return item.to || item.url;
    },
    shouldOpenInBackground(event, item) {
      // TEMP_INTERNAL_TABS_OFF_START
      // Keep native browser middle/Ctrl/Cmd behavior instead of forcing internal app tabs.
      if (INTERNAL_TABS_TEMP_DISABLED) {
        return false;
      }
      // TEMP_INTERNAL_TABS_OFF_END

      if (!isBackgroundIntent(event)) {
        return false;
      }

      const isInternalItem = !!item.to || this.isInternalRoute(item);
      const isLeaf = !item.items;
      const isSelfTarget = !item.target || item.target === "_self";
      return isInternalItem && isLeaf && isSelfTarget;
    },
    onMenuItemAuxClick(event, item, index) {
      if (event.button !== 1) {
        return;
      }

      this.onMenuItemClick(event, item, index);
    },
    onMenuItemClick(event, item, index) {
      if (item.disabled) {
        event.preventDefault();
        return;
      }

      if (this.shouldOpenInBackground(event, item)) {
        event.preventDefault();
        this.$emit("menuitem-click", {
          originalEvent: event,
          item: item,
          newTab: true,
        });
        return;
      }

      if (!item.to && !item.url) {
        event.preventDefault();
      }

      //execute command
      if (item.command) {
        item.command({ originalEvent: event, item: item });
      }

      this.activeIndex = index === this.activeIndex ? null : index;

      this.$emit("menuitem-click", {
        originalEvent: event,
        item: item,
      });
    },
    visible(item) {
      return typeof item.visible === "function"
        ? item.visible()
        : item.visible !== false;
    },
  },
  components: {
    AppSubmenu: this,
  },
};
</script>

<style scoped></style>
